import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ShieldCheck, Gear, Gavel } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Settings() {
    const { user, refresh, logout } = useAuth();
    const [lang, setLang] = useState("en");
    const [citationStyle, setCitationStyle] = useState("indian");
    const [voice, setVoice] = useState("alloy");

    useEffect(() => {
        setLang(user?.preferences?.language || "en");
        setCitationStyle(user?.preferences?.citation_style || "indian");
        setVoice(user?.preferences?.voice || "alloy");
    }, [user]);

    const save = async () => {
        await authApi.updatePrefs({ language: lang, citation_style: citationStyle, voice });
        await refresh();
        toast.success("Settings saved");
    };

    return (
        <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="settings-page">
            <div className="rule-line pt-3 mb-8">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Chambers</div>
                <h1 className="font-serif text-4xl tracking-tight">Settings</h1>
            </div>

            <div className="border border-border rounded-md bg-white divide-hairline">
                <Section title="Profile" icon={<Gavel size={16} weight="duotone" />}>
                    <div className="flex items-center gap-4">
                        {user?.picture && <img src={user.picture} alt="" className="w-12 h-12 rounded-full ring-1 ring-border" />}
                        <div>
                            <div className="font-serif text-xl">{user?.name}</div>
                            <div className="text-sm text-muted-foreground">{user?.email}</div>
                        </div>
                    </div>
                </Section>

                <Section title="Preferences" icon={<Gear size={16} weight="duotone" />}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <L label="Default answer language">
                            <Select value={lang} onValueChange={setLang}>
                                <SelectTrigger data-testid="settings-language"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="en">English</SelectItem>
                                    <SelectItem value="hi">हिन्दी</SelectItem>
                                    <SelectItem value="mixed">Mixed HI + EN</SelectItem>
                                </SelectContent>
                            </Select>
                        </L>
                        <L label="Citation style">
                            <Select value={citationStyle} onValueChange={setCitationStyle}>
                                <SelectTrigger data-testid="settings-citation"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="indian">Indian (SCC / AIR)</SelectItem>
                                    <SelectItem value="bluebook">Bluebook</SelectItem>
                                </SelectContent>
                            </Select>
                        </L>
                        <L label="Voice output">
                            <Select value={voice} onValueChange={setVoice}>
                                <SelectTrigger data-testid="settings-voice"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="alloy">Alloy — neutral</SelectItem>
                                    <SelectItem value="nova">Nova — energetic</SelectItem>
                                    <SelectItem value="onyx">Onyx — authoritative</SelectItem>
                                    <SelectItem value="sage">Sage — measured</SelectItem>
                                    <SelectItem value="shimmer">Shimmer — bright</SelectItem>
                                </SelectContent>
                            </Select>
                        </L>
                    </div>
                    <div className="mt-4"><Button onClick={save} data-testid="save-preferences-btn">Save preferences</Button></div>
                </Section>

                <Section title="Security" icon={<ShieldCheck size={16} weight="duotone" />}>
                    <div className="text-sm text-foreground/80 leading-relaxed">
                        Your session uses Emergent-managed Google OAuth with secure httpOnly cookies.
                        Session expires automatically after 7 days of inactivity.
                    </div>
                    <div className="mt-4">
                        <Button variant="outline" onClick={logout} data-testid="settings-logout-btn">Sign out</Button>
                    </div>
                </Section>
            </div>
        </div>
    );
}

function Section({ title, icon, children }) {
    return (
        <div className="p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">
                {icon}
                {title}
            </div>
            {children}
        </div>
    );
}

function L({ label, children }) {
    return (
        <div className="space-y-1">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
            {children}
        </div>
    );
}
